/**
 * Source pane: the LaTeX source of the document (what its .tex file contains), generated by the
 * server from the live editor state, shown beside the writing area with syntax colours
 * (texhighlight.ts: a coloured copy of the text lies under a transparent textarea). The server
 * sends a *source map* with it — the character range every top-level paragraph was written to
 * (sourcemap.ts) — so the two views are mirrored paragraph-exactly:
 *
 * - cursor and selection: the document's selection is drawn in the source (a thin bar at its
 *   head, the range tinted) and the textarea's own caret is put there while nobody types in it;
 *   the caret / selection of the source puts the document's selection at the same words (drawn as
 *   a mirror caret / tint while the editor has no focus). The words before the cursor are matched
 *   within the paragraph's own range only, so repeated phrases cannot mislead. The pane that has
 *   the keyboard leads; the other follows.
 * - scrolling: the paragraph at the top of one view and how far it is scrolled into puts the other
 *   view at the same place; a scroll the pane caused itself is recognised (its exact target) and
 *   not answered, so the two never chase each other.
 * - editing: the source is applied to the document as one types (parsed on the server and merged
 *   as a diff, so untouched paragraphs keep their identity), held back while the LaTeX is
 *   unbalanced — the problem, and its line, are named in the pane's foot; Ctrl+Enter applies at
 *   once. The spans are carried through the edits typed, so the mirroring keeps working while the
 *   text is ahead of the document. When the source is regenerated the caret and the scroll
 *   position stay where they were (mapped through the change).
 * Undo / redo in the pane is our own (codearea.ts, the browser's breaks when the text is
 * regenerated), and the bracket pair at the cursor is marked in the colours.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { EditorView } from 'prosemirror-view';
import type { Node as PMNode, ResolvedPos } from 'prosemirror-model';
import type * as Y from 'yjs';
import { writeLyx, pmToLyxBody, checkTexHealth, lintTex } from '@overlyx/core';
import { api } from '../api';
import { highlightTexBlocks, highlightLineList } from './texhighlight';
import { UndoStack, undoRedoKey, applyUndoRedo, applySnapshot, editingKey, matchBrackets, commentMask } from './codearea';
import { findSourceLine, findSourceOffset, locateSourceLine, locateSourceCaret, type LocateBlock, type CursorContext } from './sourcelocate';
import { spanIndexAt, spansFit, editRange, mapOffset, mapSpans, sourceOffsetInSpan, locateInSpan, lineOfOffset, offsetOfLine, type Span } from './sourcemap';
import { editorContext } from '../editor/context';
import { setMirrorCaret } from '../editor/plugins/mirrorcaret';
import { openSourceRewrite } from '../editor/ai/rewrite';
import { getPrefs } from '../prefs';
import { TextSelection } from 'prosemirror-state';
import type { LyxMathField } from '../editor/lyxmath/field';

export interface SourceTarget { view: EditorView; ydoc: Y.Doc; docId: string }

/** line height of the source text (px; styles.css `.source-pane .code`) */
const LINE_H = 15;
const PAD_TOP = 6;

function metaOf(ydoc: Y.Doc) {
  const m = ydoc.getMap<string>('meta');
  const parse = (k: string, def: unknown) => { try { const v = m.get(k); return v ? JSON.parse(v) : def; } catch { return def; } };
  return {
    preamble: parse('preamble', []) as string[],
    format: parse('format', 643) as number,
    header: parse('header', []) as string[],
    trailer: parse('trailer', []) as string[],
  };
}

/**
 * The document in LyX's file format, generated in the browser (no server needed): the fallback
 * for keeping unsynced offline edits when the server cannot store them — the file can be imported
 * into a project again.
 */
export function generateLyx(t: SourceTarget): string {
  const meta = metaOf(t.ydoc);
  return writeLyx({ preamble: ['#LyX 2.5 created this file. For more info see https://www.lyx.org/'], format: meta.format, header: { lines: meta.header }, body: pmToLyxBody(t.view.state.doc), trailer: meta.trailer });
}

/** what sourcelocate.ts needs to know about a document position: the paragraph text around it, or the formula's row when in math */
export function cursorContext($pos: ResolvedPos, field: LyxMathField | null, prev: number | null): CursorContext | null {
  const par = $pos.parent;
  if (!par.isTextblock) return null;
  const atom = String.fromCharCode(0);
  const before = par.textBetween(0, $pos.parentOffset, undefined, atom);
  const after = par.textBetween($pos.parentOffset, par.content.size, undefined, atom);
  const first = par.firstChild;
  const parStart = first?.isText ? first.text!.slice(0, 48) : undefined;
  let formula: { latex: string; row: number; display: boolean } | undefined;
  if (field?.hasFocus()) formula = { latex: field.latex.replace(/^\$|\$$/g, ''), row: field.topRow(), display: field.display };
  return { before, after, parStart, formula, prev };
}

/** the source line of the cursor (see sourcelocate.ts): the text around it, or the formula's row when in math — without a source map (SyncTeX uses it on the built LaTeX) */
export function cursorLine(view: EditorView, text: string, field: LyxMathField | null, prev: number | null): { line: number; offset: number } | null {
  const ctx = cursorContext(view.state.selection.$from, field, prev);
  return ctx ? findSourceLine(text, ctx) : null;
}

/** A paragraph or display formula of the document as the locator sees it, with the document position of every character of its text. */
export interface DocBlock extends LocateBlock { pos: number; map?: number[] }
/**
 * The document's blocks for `locateSourceLine` / `locateSourceCaret`: every text block's direct
 * children (text as is, any other inline node as one NUL character) with a map from character
 * offsets to document positions — nested insets are blocks of their own.
 */
export function docBlocks(view: EditorView): DocBlock[] {
  const blocks: DocBlock[] = [];
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === 'math_display') { blocks.push({ kind: 'math', text: String(node.attrs.latex ?? ''), pos }); return false; }
    if (node.isTextblock) {
      let text = '';
      const map: number[] = [];
      node.forEach((child, off) => {
        const start = pos + 1 + off;
        if (child.isText) { const t = child.text!; for (let i = 0; i < t.length; i++) { text += t[i]; map.push(start + i); } }
        else { text += '\u0000'; map.push(start); }
      });
      map.push(pos + 1 + node.content.size);
      blocks.push({ kind: 'text', text, pos: pos + 1, map });
    }
    return true;
  });
  return blocks;
}
/** the document position of character `offset` of a block */
export const blockPos = (b: DocBlock, offset: number): number => (b.map ? b.map[Math.max(0, Math.min(offset, b.map.length - 1))] : b.pos + offset);
const blockDocPos = (b: DocBlock, offset: number): number => (b.kind === 'math' ? b.pos : blockPos(b, offset));

/** the position of the document's top-level child `i` */
const childPos = (doc: PMNode, i: number): number => { let pos = 0; for (let k = 0; k < i && k < doc.childCount; k++) pos += doc.child(k).nodeSize; return pos; };

/**
 * The source offset of a document position: with a source map, inside its paragraph's span (its
 * words matched there only; a paragraph without output: the next one's start); without, the
 * words matched in the whole text. Null when nothing can be said.
 */
function sourceOffsetOf(text: string, spans: Span[] | null, $pos: ResolvedPos, field: LyxMathField | null, prev: number | null): number | null {
  const ctx = cursorContext($pos, field, prev);
  if (spansFit(spans, $pos.doc.childCount)) {
    const i = Math.min($pos.index(0), spans.length - 1);
    const span = spans[i];
    if (span) return ctx ? sourceOffsetInSpan(text, span, ctx) : span.start;
    for (let k = i + 1; k < spans.length; k++) if (spans[k]) return spans[k]!.start;
    for (let k = i - 1; k >= 0; k--) if (spans[k]) return spans[k]!.end;
    return null;
  }
  return ctx ? findSourceOffset(text, ctx) : null;
}

/**
 * The document position a source offset refers to: with a source map, the paragraph the offset
 * belongs to is known — the words of the line before it are looked for among that paragraph's
 * own blocks (nested insets included); without, among all blocks. Null when the source is not in
 * the document.
 */
function docPosOf(text: string, spans: Span[] | null, offset: number, view: EditorView, blocks: DocBlock[]): number | null {
  const doc = view.state.doc;
  if (spansFit(spans, doc.childCount)) {
    const i = spanIndexAt(spans, offset, text);
    if (i < 0) return null;
    const start = childPos(doc, i), par = doc.child(i);
    const parStart = start + 1, parEnd = start + par.nodeSize - 1;
    const inner = blocks.filter(b => b.pos >= parStart && b.pos <= parEnd);
    const span = spans[i];
    if (!inner.length || !span || offset < span.start || offset > span.end) return parStart;
    const hit = locateInSpan(text, span, offset, inner);
    return blockDocPos(inner[hit.index], hit.offset);
  }
  const line = lineOfOffset(text, offset), col = offset - (text.lastIndexOf('\n', offset - 1) + 1);
  const hit = locateSourceCaret(text, line, col, blocks);
  return hit ? blockDocPos(blocks[hit.index], hit.offset) : null;
}

function storedWidth(): number {
  try { const v = Number(localStorage.getItem('ol.source.w')); return v >= 280 ? v : 520; } catch { return 520; }
}

/** a problem or note about the source, with where it is (null: nowhere in particular) */
interface Remark { note: string; at: number | null }
/** the offset a message names ("… on line 12 …"), when it names one */
function offsetNamed(text: string, message: string): number | null {
  const m = /\bline (\d+)\b/.exec(message);
  return m ? offsetOfLine(text, Number(m[1]) - 1) : null;
}

/** The LaTeX source beside the document (Ctrl+Alt+S / the "[raw]" tab), with synchronized scrolling, cursor and selection, and live apply. */
export function SourcePane({ target, tick, selTick, mathField, onNotify, onClose, onSave, fill, style }: { target: SourceTarget | null; tick: number; selTick?: number; /** the formula being edited, if any */ mathField?: LyxMathField | null; onNotify: (msg: string, kind?: 'info' | 'error') => void; onClose?: () => void; onSave?: () => void; /** a pane of the web client's pane row (app/panes.ts): its width comes from there, no own width or grip */ fill?: boolean; style?: Record<string, string | number> }) {
  const [text, setText] = useState('');
  const [refreshFailed, setRefreshFailed] = useState(false);
  const refreshFailedRef = useRef(false);
  const markRefreshFailed = (failed: boolean) => { refreshFailedRef.current = failed; setRefreshFailed(failed); };
  /** the text as typed, ahead of the render (a live apply scheduled by a keystroke must send it, not the previous render's) */
  const textRef = useRef('');
  /** the source map for `textRef.current`: the range of every top-level paragraph, carried through the edits typed (null: none) */
  const spansRef = useRef<Span[] | null>(null);
  /** the offsets the lines start at (`textRef.current`), for the scroll sync */
  const lineStartsRef = useRef<number[]>([0]);
  /** the generated source has no \begin{document}: a child document — the health check must not ask for one */
  const fragmentRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const markDirty = (d: boolean) => { dirtyRef.current = d; setDirty(d); };
  const inFlight = useRef<Promise<unknown> | null>(null);
  const [curLine, setCurLine] = useState<number | null>(null);
  /** the document selection's head as a character offset in the source (a caret mark in the coloured copy) */
  const [curOff, setCurOff] = useState<number | null>(null);
  /** the document's selected range in the source (an overlay tint), when it selects something */
  const [curSel, setCurSel] = useState<{ from: number; to: number } | null>(null);
  /** after the source caret moved the document cursor, the echo (document → source) is skipped for a moment */
  const caretLock = useRef(0);
  const lastOffset = useRef<number | null>(null);
  const [width, setWidth] = useState(storedWidth);
  const focused = useRef(false);
  const [hasFocus, setHasFocus] = useState(false);
  const pendingReload = useRef(false);
  /** live apply: what happened to the last edited source */
  const [applied, setApplied] = useState<'idle' | 'waiting' | 'applying' | 'ok' | 'held' | 'error'>('idle');
  const [applyNote, setApplyNote] = useState('');
  /** what holds the live apply back (the linter's findings, a server error), each with its place */
  const [problems, setProblems] = useState<Remark[]>([]);
  /** what the parser noted about the last applied source (constructs kept as raw LaTeX) */
  const [notes, setNotes] = useState<Remark[]>([]);
  const applyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const pre = useRef<HTMLPreElement>(null);
  const selPre = useRef<HTMLPreElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);
  const targetRef = useRef(target);
  targetRef.current = target;
  const sourceOwner = useRef<SourceTarget | null>(target);
  const sameTarget = (a: SourceTarget | null, b: SourceTarget | null) => !!a && !!b
    && a.docId === b.docId && a.view === b.view && a.ydoc === b.ydoc;
  const ownsSource = () => sameTarget(sourceOwner.current, target) && sameTarget(targetRef.current, target);
  const holdPreviousSource = () => {
    const note = 'Source belongs to a previous editor. Return to it, or revert to load the current source.';
    setApplied('held'); setApplyNote(note); setProblems([{ note, at: null }]);
  };
  // A response belongs to this target only, including before deferred effects run.
  useLayoutEffect(() => {
    seq.current++;
    if (!textRef.current && !dirtyRef.current) sourceOwner.current = target;
    markRefreshFailed(!!textRef.current);
    return () => { seq.current++; };
  }, [target?.docId, target?.view, target?.ydoc]);
  const undo = useRef(new UndoStack({ value: '', start: 0, end: 0 }));
  /** the textarea's selection while it has the focus (null otherwise) */
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);
  const cursor = sel && sel.start === sel.end ? sel.start : null;
  /** the document's blocks, computed once per document version */
  const blocksCache = useRef<{ doc: PMNode | null; blocks: DocBlock[] }>({ doc: null, blocks: [] });
  const blocksOf = (view: EditorView): DocBlock[] => { const c = blocksCache.current; if (c.doc !== view.state.doc) { c.doc = view.state.doc; c.blocks = docBlocks(view); } return c.blocks; };
  const mask = useMemo(() => commentMask(text), [text]);
  const match = useMemo(() => (cursor === null ? null : matchBrackets(text, cursor, mask)), [text, cursor, mask]);
  useEffect(() => { const starts = [0]; for (let i = text.indexOf('\n'); i >= 0; i = text.indexOf('\n', i + 1)) starts.push(i + 1); lineStartsRef.current = starts; }, [text]);
  // the coloured copy, one HTML string per line; the <pre> is patched line by line (see below) —
  // replacing its whole innerHTML on every keystroke costs the browser tens of ms for a long source
  const lineHtml = useMemo(() => {
    let marks: Map<number, string> | undefined;
    if (match) {
      marks = new Map();
      const cls = match.kind === 'adjacent' ? 'hl-match' : 'hl-enclose';
      for (const at of [match.open, match.close]) for (let k = 0; k < match.len; k++) marks.set(at + k, cls);
    }
    // the document cursor as a thin bar before the character at its offset (after the last one of a line)
    if (curOff !== null && !dirty && !refreshFailed && text) {
      marks ??= new Map();
      const at = Math.min(curOff, text.length);
      if (at < text.length && text[at] !== '\n') { if (!marks.has(at)) marks.set(at, 'hl-caret'); }
      else if (at > 0 && text[at - 1] !== '\n' && !marks.has(at - 1)) marks.set(at - 1, 'hl-caret-after');
    }
    return highlightLineList(highlightTexBlocks(text, marks));
  }, [text, match, curOff, dirty, refreshFailed]);
  const shownLines = useRef<string[]>([]);
  useEffect(() => {
    const code = pre.current?.firstElementChild as HTMLElement | null;
    if (!code) return;
    const prev = shownLines.current;
    const divs = code.children;
    const n = Math.min(prev.length, lineHtml.length, divs.length);
    for (let i = 0; i < n; i++) if (prev[i] !== lineHtml[i]) divs[i].innerHTML = lineHtml[i];
    for (let i = divs.length - 1; i >= lineHtml.length; i--) divs[i].remove();
    if (lineHtml.length > divs.length) {
      const frag = document.createDocumentFragment();
      for (let i = divs.length; i < lineHtml.length; i++) { const d = document.createElement('div'); d.className = 'l'; d.innerHTML = lineHtml[i]; frag.append(d); }
      code.append(frag);
    }
    shownLines.current = lineHtml;
  }, [lineHtml]);
  // the current line's mark
  useEffect(() => {
    const code = pre.current?.firstElementChild as HTMLElement | null;
    if (!code) return;
    code.querySelector('.l.cur')?.classList.remove('cur');
    const want = dirty || refreshFailed ? null : curLine;
    if (want !== null && code.children[want]) code.children[want].classList.add('cur');
  }, [curLine, dirty, lineHtml, refreshFailed]);
  const updateSel = () => { const el = ta.current; if (el) setSel(document.activeElement === el ? { start: el.selectionStart, end: el.selectionEnd } : null); };
  useEffect(() => {
    const h = () => { if (document.activeElement === ta.current) updateSel(); };
    document.addEventListener('selectionchange', h);
    return () => document.removeEventListener('selectionchange', h);
  }, []);

  // the coloured copy (and the selection overlay) scroll with the textarea
  const syncScroll = () => {
    const el = ta.current;
    if (!el) return;
    for (const p of [pre.current, selPre.current]) if (p) { p.scrollTop = el.scrollTop; p.scrollLeft = el.scrollLeft; }
  };
  /* scrolls the pane makes itself are recognised by their exact target and not answered (no chasing) */
  const expected = useRef({ doc: -1, src: -1 });
  const scrollSrc = (y: number) => { const el = ta.current; if (!el) return; el.scrollTop = y; expected.current.src = el.scrollTop; syncScroll(); };
  const scrollDoc = (scrollEl: HTMLElement, y: number) => { scrollEl.scrollTop = y; expected.current.doc = scrollEl.scrollTop; };

  /** Fetch the source (with its map) and show it — the caret and the scroll position stay where they were, mapped through the change. */
  const load = async () => {
    if (!target) return;
    const my = ++seq.current;
    try {
      let t: string, spans: Span[] | null = null;
      try { const r = await api.texMap(target.docId); t = r.text; spans = r.spans; }
      catch { t = await api.texText(target.docId); }   // a server without the map
      if (my !== seq.current) return;
      if (dirtyRef.current || focused.current) { pendingReload.current = true; return; }
      sourceOwner.current = target;
      markRefreshFailed(false);
      const el = ta.current;
      const e = editRange(textRef.current, t);
      const s0 = el ? mapOffset(el.selectionStart, e) : 0, s1 = el ? mapOffset(el.selectionEnd, e, el.selectionStart !== el.selectionEnd) : 0;
      const scroll = el?.scrollTop ?? 0;
      textRef.current = t; spansRef.current = spans; fragmentRef.current = !/^\\begin\{document\}/m.test(t);
      setText(t);
      if (el) { el.value = t; try { el.setSelectionRange(Math.min(s0, s1), Math.max(s0, s1)); } catch { /* ignore */ } el.scrollTop = scroll; syncScroll(); }
      undo.current.reset({ value: t, start: s0, end: s1 });
    } catch {
      if (my !== seq.current) return;
      if (dirtyRef.current || focused.current) { pendingReload.current = true; return; }
      // An unavailable server is not source text: keep the last preview and its history.
      markRefreshFailed(true);
    }
  };
  /** the exact map for the text as it stands (after a live apply) — only when the server writes exactly what was typed; else the spans carried along stay */
  const refreshMap = async (forText: string) => {
    if (!target) return;
    const my = seq.current;
    try { const r = await api.texMap(target.docId); if (my === seq.current && ownsSource() && textRef.current === forText && r.text === forText && r.spans) spansRef.current = r.spans; } catch { /* keep what we have */ }
  };

  // the document settings change the source too (class, packages, the preamble): they live in the
  // CRDT's meta map, not in the document the `tick` follows
  const [settingsTick, setSettingsTick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const settings = target.ydoc.getMap('meta');
    const changed = () => setSettingsTick(t => t + 1);
    settings.observe(changed);
    return () => settings.unobserve(changed);
  }, [target?.ydoc]);

  // regenerate the source when the document changes — not while the user is editing it or has the
  // keyboard in it (a live apply changes the document; replacing the text under the cursor would be
  // disruptive): then it is regenerated when the pane loses the focus
  useEffect(() => {
    if (!target) return;
    if (dirty || focused.current) { pendingReload.current = true; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void load(); }, 700);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [target?.docId, tick, settingsTick, dirty]);

  // follow the document's selection: its head marked (a bar) and kept in view, its range tinted, and
  // the textarea's own selection put there — not while the source is being edited or has the keyboard
  // (then the source leads and the document follows, below)
  useEffect(() => {
    if (!target || dirty || !text || refreshFailedRef.current) return;
    if (caretLock.current > Date.now()) return;   // the document cursor was just set from the source caret
    const el = ta.current;
    if (el && document.activeElement === el) return;
    const view = target.view;
    const { $from, $to, $head, empty } = view.state.selection;
    const spans = spansRef.current;
    const head = sourceOffsetOf(text, spans, $head, mathField ?? null, lastOffset.current);
    lastOffset.current = head;
    setCurLine(head === null ? null : lineOfOffset(text, head));
    setCurOff(head);
    let range: { from: number; to: number } | null = null;
    if (!empty && head !== null) {
      const other = $head.pos === $from.pos ? $to : $from;
      const o = sourceOffsetOf(text, spans, other, null, head);
      if (o !== null && o !== head) range = { from: Math.min(o, head), to: Math.max(o, head) };
    }
    setCurSel(range);
    if (head !== null && el && el.value === text) {
      try { range ? el.setSelectionRange(range.from, range.to, head === range.to ? 'forward' : 'backward') : el.setSelectionRange(head, head); } catch { /* ignore */ }
    }
  }, [selTick, text, dirty, mathField, refreshFailed]);
  // the other way: the caret / selection in the source (a click, arrow keys, a drag) puts the document's
  // selection at those words and scrolls it into view; the editor keeps drawing it as a mirror while it has no focus
  useEffect(() => {
    if (!target || !sel || !text || refreshFailedRef.current) return;
    const el = ta.current;
    if (!el || document.activeElement !== el) return;
    const t = setTimeout(() => {
      if (refreshFailedRef.current) return;
      const view = target.view;
      const spans = spansRef.current;
      const blocks = blocksOf(view);
      const headOff = el.selectionDirection === 'backward' ? sel.start : sel.end;
      const anchorOff = headOff === sel.end ? sel.start : sel.end;
      const head = docPosOf(text, spans, headOff, view, blocks);
      if (head === null) return;
      const anchor = anchorOff === headOff ? head : docPosOf(text, spans, anchorOff, view, blocks);
      caretLock.current = Date.now() + 500;
      setCurLine(lineOfOffset(text, headOff)); setCurOff(headOff); setCurSel(null); lastOffset.current = headOff;
      try {
        const size = view.state.doc.content.size;
        const $head = view.state.doc.resolve(Math.min(head, size));
        const selection = anchor === null || anchor === head ? TextSelection.near($head) : TextSelection.between(view.state.doc.resolve(Math.min(anchor, size)), $head);
        view.dispatch(view.state.tr.setSelection(selection).setMeta('addToHistory', false));
        setMirrorCaret(view, { from: selection.from, to: selection.to, head: selection.head });
        // bring the cursor into view ourselves: the editor's scrollIntoView follows the DOM selection, which is in the textarea
        const scrollEl = view.dom.closest('.editor-scroll') as HTMLElement | null;
        if (scrollEl) {
          const c = view.coordsAtPos(selection.head), r = scrollEl.getBoundingClientRect();
          if (c.top < r.top + 24 || c.bottom > r.bottom - 24) scrollDoc(scrollEl, scrollEl.scrollTop + c.top - r.top - r.height / 3);
        }
      } catch { /* not a valid position any more */ }
    }, 120);
    return () => clearTimeout(t);
  }, [sel, text, refreshFailed]);
  // A stale source preview cannot locate a caret in the current document.
  useEffect(() => {
    if (!refreshFailed || !target) return;
    try { setMirrorCaret(target.view, null); } catch { /* the view may have gone */ }
  }, [refreshFailed, target?.view]);
  // the mirror caret goes when the pane is left
  useEffect(() => () => { const v = target?.view; if (v && v.dom.isConnected) { try { setMirrorCaret(v, null); } catch { /* ignore */ } } }, [target?.docId]);
  // keep the marked line in view (once it is rendered; lines wrap, so it is measured, not computed)
  useEffect(() => {
    const el = ta.current, cur = pre.current?.querySelector('.l.cur') as HTMLElement | null;
    if (!el || !cur || dirty || document.activeElement === el) return;
    const y = cur.offsetTop, h = cur.offsetHeight || LINE_H;
    if (y < el.scrollTop + h || y + h > el.scrollTop + el.clientHeight - h) scrollSrc(Math.max(0, y - el.clientHeight / 3));
  }, [curLine, lineHtml]);
  // the selection overlay is laid out like the textarea: scroll it along once it exists
  const overlay = curSel && !dirty && !hasFocus && !refreshFailed ? curSel : null;
  useEffect(() => { syncScroll(); }, [overlay]);

  useEffect(() => { try { localStorage.setItem('ol.source.w', String(Math.round(width))); } catch { /* ignore */ } }, [width]);
  const startResizeW = (e: PointerEvent) => {
    e.preventDefault();
    // (kept by the grip: a frame under the pointer would take the moves and the release)
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const startX = e.clientX, startW = width;
    const move = (ev: PointerEvent) => setWidth(Math.max(280, Math.min(window.innerWidth * 0.8, startW + startX - ev.clientX)));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  /**
   * Apply the edited source to the document: parsed on the server and merged as a diff. Runs by
   * itself a moment after the last keystroke (live), but only when the source is structurally sound
   * (balanced braces, a document body — a half-typed \begin{…} must not turn the document into ERT)
   * — else it is held until it is, and the foot of the pane names the problem and its line;
   * Ctrl+Enter applies at once.
   */
  const apply = async (force = false): Promise<boolean> => {
    if (!target) return false;
    if (!ownsSource()) { holdPreviousSource(); return false; }
    if (inFlight.current) { try { await inFlight.current; } catch { /* reflected below */ } return apply(force); }
    const current = textRef.current;
    // the structure must be sound first (lintTex: braces, environments, $ and \[ \] with their lines; the health
    // check: the document's boundaries and the managed block) — a half-typed \begin{…} would turn the rest into ERT
    const found: Remark[] = [
      ...checkTexHealth(current, { isFragment: fragmentRef.current }).filter(i => i.severity === 'error').map(i => ({ note: i.message, at: null })),
      ...lintTex(current).map(i => ({ note: i.message, at: i.offset })),
    ];
    if (found.length && !force) { setApplied('held'); setApplyNote(found[0].note); setProblems(found); return false; }
    setApplied('applying'); setProblems([]);
    try {
      editorContext.sourceAppliedAt = Date.now();
      const request = api.applySource(target.docId, current);
      inFlight.current = request;
      const r = await request;
      if (!ownsSource()) { holdPreviousSource(); return false; }
      if (textRef.current !== current) { setApplied('waiting'); scheduleApply(); return false; }   // typed on meanwhile: apply again
      markDirty(false);
      setApplied('ok'); setApplyNote(r.warnings.length ? `${r.warnings.length} note${r.warnings.length > 1 ? 's' : ''}` : '');
      setNotes(r.warnings.map(w => ({ note: w, at: offsetNamed(current, w) })));
      void refreshMap(current);
      return true;
    } catch (e) {
      setApplied('error'); setApplyNote((e as Error).message); setProblems([{ note: (e as Error).message, at: null }]);
      return false;
    } finally { inFlight.current = null; }
  };
  const scheduleApply = () => {
    if (applyTimer.current) clearTimeout(applyTimer.current);
    setApplied('waiting');
    applyTimer.current = setTimeout(() => { applyTimer.current = null; void apply(); }, 1200);
  };
  useEffect(() => () => { if (applyTimer.current) clearTimeout(applyTimer.current); }, []);
  const revert = () => { if (applyTimer.current) clearTimeout(applyTimer.current); markDirty(false); setApplied('idle'); setProblems([]); setNotes([]); void load(); };
  const onBlur = () => { focused.current = false; setHasFocus(false); setSel(null); if (pendingReload.current && !dirtyRef.current) { pendingReload.current = false; void load(); } };
  /** the text was edited (typed, an editing key, undo, an AI rewrite): the map follows the edit, the document a moment later */
  const changed = (el: HTMLTextAreaElement, record = true) => {
    seq.current++;
    pendingReload.current = true;
    const prev = textRef.current, v = el.value;
    if (record) undo.current.record({ value: v, start: el.selectionStart, end: el.selectionEnd });
    if (spansRef.current) spansRef.current = mapSpans(spansRef.current, editRange(prev, v));
    textRef.current = v; setText(v); markDirty(true); setNotes([]); updateSel(); scheduleApply();
  };
  /** put the caret at a remark's place and show that line */
  const gotoOffset = (at: number) => {
    const el = ta.current;
    if (!el) return;
    const off = Math.min(at, el.value.length);
    el.focus();
    try { el.setSelectionRange(off, off); } catch { /* ignore */ }
    const line = pre.current?.querySelectorAll<HTMLElement>('.l')[lineOfOffset(el.value, off)];
    if (line) scrollSrc(Math.max(0, line.offsetTop - el.clientHeight / 3));
    updateSel();
  };
  /** the remarks of the foot: each with a "go to line" link when it has a place */
  const remarkList = (list: Remark[], max = 3) => (
    <>
      {list.slice(0, max).map((r, i) => <span class="item" key={i}>{i > 0 ? ' · ' : ''}{r.note}{r.at !== null && <button class="goto" onClick={() => gotoOffset(r.at!)}>go to line {lineOfOffset(text, r.at) + 1}</button>}</span>)}
      {list.length > max && <span class="item"> · {list.length - max} more</span>}
    </>
  );

  /* ---- the split view: the two panes scroll together — the paragraph at the top of one, and how far it is scrolled into, sets the other */
  const lineEls = () => Array.from(pre.current?.querySelectorAll<HTMLElement>('.l') ?? []);
  useEffect(() => {
    if (!target) return;
    const view = target.view;
    const scrollEl = view.dom.closest('.editor-scroll') as HTMLElement | null;
    const el = ta.current;
    if (!scrollEl || !el) return;
    let rafDoc = 0, rafSrc = 0;
    /** a scroll event at the position the pane itself scrolled to is its own */
    const own = (side: 'doc' | 'src', value: number): boolean => { const e = expected.current; const mine = e[side] >= 0 && Math.abs(e[side] - value) < 1.5; e[side] = -1; return mine; };
    // document → source
    const onDocScroll = () => {
      if (refreshFailedRef.current) return;
      if (own('doc', scrollEl.scrollTop)) return;
      cancelAnimationFrame(rafDoc);
      rafDoc = requestAnimationFrame(() => {
        if (refreshFailedRef.current) return;
        const text = textRef.current;
        if (!text) return;
        const top = scrollEl.getBoundingClientRect().top + 4;
        const pars = Array.from(view.dom.querySelectorAll<HTMLElement>(':scope > .lyx-par'));
        const par = pars.find(p => p.getBoundingClientRect().bottom > top) ?? pars[pars.length - 1];
        if (!par) return;
        const rect = par.getBoundingClientRect();
        const frac = Math.max(0, Math.min(1, (top - rect.top) / Math.max(1, rect.height)));
        const lines = lineEls();
        let y: number | null = null;
        const spans = spansRef.current;
        if (spansFit(spans, view.state.doc.childCount)) {
          let i: number;
          try { i = view.state.doc.resolve(view.posAtDOM(par, 0)).index(0); } catch { return; }
          let span: Span = null;
          for (let k = i; !span && k < spans.length; k++) span = spans[k];
          if (span) {
            const l1 = lines[lineOfOffset(text, span.start)], l2 = lines[lineOfOffset(text, span.end)];
            if (l1) { const y1 = l1.offsetTop, y2 = l2 ? l2.offsetTop + l2.offsetHeight : y1 + LINE_H; y = y1 + frac * Math.max(0, y2 - y1); }
          }
        }
        if (y === null) {   // no map: the paragraph's words
          let ptext = '';
          try { const $p = view.state.doc.resolve(view.posAtDOM(par, 0)); const node = $p.node($p.depth >= 1 ? 1 : 0); ptext = node.textBetween(0, node.content.size, undefined, '\u0000'); } catch { return; }
          if (!ptext.trim()) return;
          const found = findSourceLine(text, { before: '', after: ptext.slice(0, 200), parStart: ptext.slice(0, 48) });
          if (!found) return;
          y = lines[found.line]?.offsetTop ?? found.line * LINE_H;
        }
        scrollSrc(Math.max(0, y - PAD_TOP));
      });
    };
    // source → document
    const onSrcScroll = () => {
      if (refreshFailedRef.current) return;
      if (own('src', el.scrollTop)) return;
      cancelAnimationFrame(rafSrc);
      rafSrc = requestAnimationFrame(() => {
        if (refreshFailedRef.current) return;
        const text = textRef.current;
        if (!text) return;
        const lines = lineEls();
        if (!lines.length) return;
        let lo = 0, hi = lines.length - 1, line = 0;
        while (lo <= hi) { const mid = (lo + hi) >> 1; if (lines[mid].offsetTop + lines[mid].offsetHeight > el.scrollTop + 1) { line = mid; hi = mid - 1; } else lo = mid + 1; }
        const doc = view.state.doc;
        const spans = spansRef.current;
        let par: HTMLElement | null = null, frac = 0;
        if (spansFit(spans, doc.childCount)) {
          const off = lineStartsRef.current[line] ?? offsetOfLine(text, line);
          const i = spanIndexAt(spans, off, text);
          if (i < 0) return;
          const span = spans[i];
          if (span) {
            const a = lineOfOffset(text, span.start), b = lineOfOffset(text, span.end);
            const within = (el.scrollTop - lines[line].offsetTop) / Math.max(1, lines[line].offsetHeight);
            frac = Math.max(0, Math.min(1, (line - a + Math.max(0, within)) / (b - a + 1)));
          }
          const dom = view.nodeDOM(childPos(doc, i));
          par = dom instanceof HTMLElement ? dom : null;
        } else {
          const blocks = blocksOf(view);
          const hit = locateSourceLine(text, line, blocks);
          if (!hit) return;
          const b = blocks[hit.index];
          let dom: Node | null = null;
          try { dom = b.kind === 'math' ? view.nodeDOM(b.pos) : view.domAtPos(b.pos).node; } catch { return; }
          par = (dom instanceof Element ? dom : dom?.parentElement)?.closest('.lyx-par, .lyx-math-display') as HTMLElement | null;
        }
        if (!par) return;
        const rect = par.getBoundingClientRect();
        scrollDoc(scrollEl, scrollEl.scrollTop + rect.top + frac * rect.height - scrollEl.getBoundingClientRect().top - 8);
      });
    };
    scrollEl.addEventListener('scroll', onDocScroll);
    el.addEventListener('scroll', onSrcScroll);
    return () => { scrollEl.removeEventListener('scroll', onDocScroll); el.removeEventListener('scroll', onSrcScroll); cancelAnimationFrame(rafDoc); cancelAnimationFrame(rafSrc); };
  }, [target?.docId]);

  const name = target?.docId.split('/').pop() ?? '';
  // what the file is written in: LaTeX, markdown, or an HTML article's page
  const lang = /\.html?$/i.test(name) ? 'HTML' : /\.(md|markdown)$/i.test(name) ? 'Markdown' : 'LaTeX';
  const showProblems = (applied === 'held' || applied === 'error') && problems.length > 0;
  const showNotes = !showProblems && !dirty && notes.length > 0;
  return (
    <div class={'source-pane right' + (fill ? ' fill' : '')} data-pane={fill ? 'tex' : undefined} style={fill ? style : { width: width + 'px' }}>
      {!fill && <div class="grip v" title="Drag to resize" onPointerDown={startResizeW} />}
      <div class="bar">
        <span class="small-btn active" title={lang === 'LaTeX' ? 'The LaTeX source of the document (its .tex file)' : `The ${lang === 'HTML' ? 'page\'s HTML' : 'markdown'} source of the document (its file)`}>{lang}</span>
        <span class="name" title={target?.docId}>{name}</span>
        <span style="flex:1" />
        {refreshFailed && <span class="apply-state error" role="status" data-source-refresh="stale">{ownsSource() ? 'Source preview out of date' : 'Source belongs to a previous editor'}</span>}
        {refreshFailed && <button class="small-btn" disabled={dirty} onClick={() => { void load(); }} title="Retry the source refresh. Existing text is kept if the server is unavailable.">Retry source</button>}
        {dirty && <span class={'apply-state ' + applied} title={applyNote} data-apply-state={applied}>{applied === 'waiting' ? 'editing…' : applied === 'applying' ? 'applying…' : applied === 'held' ? 'not applied: ' + applyNote : applied === 'error' ? 'not applied: ' + applyNote : ''}</span>}
        {dirty && <button class="small-btn" onClick={revert} title="Drop the edits and regenerate the source from the document">Revert</button>}
        {!dirty && applied === 'ok' && <span class="apply-state ok" data-apply-state="ok" title={applyNote}>applied ✓{applyNote ? ' · ' + applyNote : ''}</span>}
        {onClose && <button class="small-btn close" onClick={onClose} title="Hide the source pane (Ctrl+Alt+S)">✕</button>}
      </div>
      {/* line numbers sit in the left padding, as wide as the last line's number */}
      <div class="code" style={`--gutter: ${String(lineHtml.length).length + 1}ch`}>
        <pre class="hl" ref={pre} aria-hidden="true"><code /></pre>
        {overlay && <pre class="hl selhl" ref={selPre} aria-hidden="true"><code>{text.slice(0, overlay.from)}<span class="selrange">{text.slice(overlay.from, overlay.to)}</span>{text.slice(overlay.to)}</code></pre>}
        {/* uncontrolled: the browser inserts what is typed and the DOM is the source of truth (a controlled value re-applied by a slow re-render can drop a fast keystroke); load() sets it */}
        <textarea ref={ta} class="source" spellcheck={false} onScroll={syncScroll} onClick={updateSel} onKeyUp={updateSel} onSelect={updateSel}
          onFocus={() => { focused.current = true; setHasFocus(true); updateSel(); }} onBlur={onBlur}
          onInput={e => changed(e.target as HTMLTextAreaElement)}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 's') {
              e.preventDefault(); e.stopPropagation();
              if (applyTimer.current) clearTimeout(applyTimer.current);
              void apply(true).then(ok => { if (ok) onSave?.(); });
              return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && dirty) { e.preventDefault(); if (applyTimer.current) clearTimeout(applyTimer.current); void apply(true); return; }
            // ⌘J in the source view: rewrite the selected LaTeX (or write at the cursor) with AI
            if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'j' && getPrefs().aiRewrite && target) {
              e.preventDefault();
              const el = ta.current!;
              const line = lineOfOffset(el.value, el.selectionStart);
              const anchor = (pre.current?.querySelectorAll<HTMLElement>('.l')[line] ?? el).getBoundingClientRect();
              openSourceRewrite({
                docId: target.docId, host: document.body, textarea: el, anchor,
                onAccept: (tex, from, to) => {
                  const v = el.value.slice(0, from) + tex + el.value.slice(to);
                  el.value = v;
                  const at = from + tex.length;
                  el.setSelectionRange(at, at);
                  changed(el);
                  el.focus();
                },
              });
              return;
            }
            const ur = undoRedoKey(e);
            if (ur) { e.preventDefault(); const s = applyUndoRedo(ta.current!, undo.current, ur); if (s) { ta.current!.value = s.value; changed(ta.current!, false); } return; }
            if (e.isComposing) return;
            const el = ta.current!;
            const s = editingKey(e, el);
            if (s) { e.preventDefault(); applySnapshot(el, s); changed(el); }
          }} />
      </div>
      <div class={'hint' + (showProblems ? ' problem' : showNotes ? ' notes' : '')}>
        {showProblems ? <>{'Not applied: '}{remarkList(problems)}</>
          : showNotes ? <>{'Applied — '}{remarkList(notes)}</>
          : dirty ? `Edits are applied to the document as you type (once the ${lang} is well-formed; Ctrl+Enter applies at once).` : `The ${lang} source beside the document: cursor, selection and scrolling are mirrored; edit here and the document follows.`}
      </div>
    </div>
  );
}
